import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Typography, Skeleton, Result, Button, Divider, Space } from 'antd';
import { ClockCircleOutlined, CalendarOutlined, SolutionOutlined, EnvironmentOutlined } from '@ant-design/icons';
import AuthShell from '../../ui/AuthShell';
import Card from '../../ui/Card';
import AuthHeader from '../../ui/AuthHeader';
import { decryptLinkPayload, getEncryptionKey } from '../../utils/encryption';
import AppStoreLinks from '../../components/AppStoreLinks';

const { Title, Text } = Typography;

const decryptJobLink = async (encryptedPart, key) => {
    try {
        const decryptedText = await decryptLinkPayload(encryptedPart, key);
        if (!decryptedText) throw new Error('Decryption returned empty.');

        const parts = decryptedText.split('||');
        if (parts.length === 3) {
            return {
                type: 'plain',
                id: parts[0],
                title: parts[1],
                clientName: parts[2]
            };
        } else if (parts.length >= 4) {
            const id = parts[0];
            const title = parts[1];
            const shift = parts[2];

            const scheduleAndEmployee = parts[3].split('|');
            const schedulePart = scheduleAndEmployee[0];
            const employeeId = scheduleAndEmployee[1] || undefined;

            const [hoursStr, daysStr] = schedulePart.split('x');
            const hoursPerDay = parseInt(hoursStr, 10);
            const daysPerWeek = parseInt(daysStr, 10);

            return {
                type: 'affiliate',
                id,
                title,
                shift,
                hoursPerDay,
                daysPerWeek,
                employeeId,
            };
        } else {
            throw new Error('Invalid payload structure.');
        }
    } catch (error) {
        console.error('Failed to decrypt job link', error);
        return { error: error.message || error.toString() };
    }
};

const JobPublicDetails = () => {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [jobData, setJobData] = useState(null);

    const [showData, setShowData] = useState(false);

    useEffect(() => {
        let isMounted = true;
        let timeoutId;

        const handleVisibilityChange = () => {
            if (document.hidden) {
                clearTimeout(timeoutId);
                // User went to the app, but we can still show data when they come back
                if (isMounted) setShowData(true);
            }
        };

        const loadData = async () => {
            if (!id) {
                setLoading(false);
                if (isMounted) setShowData(true);
                return;
            }

            const key = getEncryptionKey();
            const data = await decryptJobLink(id, key);

            if (!isMounted) return;

            setJobData(data);
            setLoading(false);

            if (data && data.error) {
                setShowData(true);
                return;
            }

            if (data && data.id) {
                document.addEventListener('visibilitychange', handleVisibilityChange);

                // Attempt to open the app via deep link immediately
                const appUrl = `vitalstaf://job/${data.id}`;
                window.location.href = appUrl;

                // Fallback to showing web data if the app is not installed or Safari blocks the auto-redirect
                // Using 3000ms gives iOS users enough time to click the "Open in app" dialogue.
                timeoutId = setTimeout(() => {
                    if (isMounted) setShowData(true);
                }, 3000);
            } else {
                setShowData(true);
            }
        };

        loadData();

        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [id]);

    if (loading) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Skeleton active paragraph={{ rows: 4 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!showData) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Title level={4}>Opening in VitalStaf App...</Title>
                    <Skeleton active paragraph={{ rows: 2 }} />
                </Card>
            </AuthShell>
        );
    }

    if (!jobData || jobData.error) {
        return (
            <AuthShell>
                <Card style={{ padding: 24, minWidth: '350px' }}>
                    <Result
                        status="error"
                        title="Link Expired or Invalid"
                        subTitle={jobData && jobData.error ? `Error: ${jobData.error}` : "Sorry, we couldn't load the job details for this link."}
                        extra={[
                            <Link key="home" to="/">
                                <Button type="primary" className="vs-btn vs-btn-primary">Go to Homepage</Button>
                            </Link>
                        ]}
                    />
                </Card>
            </AuthShell>
        );
    }

    return (
        <AuthShell>
            <Card style={{ padding: 32, minWidth: '400px', maxWidth: '500px' }}>
                <AuthHeader
                    pillText="VitalStaf Clinician Job"
                    title="Job Opportunity"
                    subtitle="You've been invited to review this position."
                />

                <Divider />

                <Title level={3} style={{ marginTop: 0, marginBottom: 24, color: '#0f453f' }}>
                    {jobData.title}
                </Title>

                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    {jobData.type === 'affiliate' ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <ClockCircleOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                                <div>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Shift</Text>
                                    <Text strong>{jobData.shift || 'Not specified'}</Text>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <CalendarOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                                <div>
                                    <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Schedule</Text>
                                    <Text strong>{jobData.hoursPerDay} hours/day &bull; {jobData.daysPerWeek} days/week</Text>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <EnvironmentOutlined style={{ fontSize: 20, color: '#16a34a', marginRight: 16 }} />
                            <div>
                                <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>Client / Facility</Text>
                                <Text strong>{jobData.clientName || 'Not specified'}</Text>
                            </div>
                        </div>
                    )}
                </Space>

                <Divider style={{ margin: '32px 0 24px 0' }} />

                <Space direction="vertical" size="middle" style={{ width: '100%', marginBottom: 16 }}>
                    <Button
                        type="default"
                        size="large"
                        block
                        className="vs-btn"
                        style={{ height: 48, fontSize: 16 }}
                        onClick={() => { window.location.href = `vitalstaf://job/${jobData.id}`; }}
                    >
                        Open in VitalStaf App
                    </Button>

                    <Link to="/auth/signup" style={{ width: '100%' }}>
                        <Button
                            type="primary"
                            size="large"
                            block
                            className="vs-btn vs-btn-primary"
                            style={{ height: 48, fontSize: 16 }}
                        >
                            Apply for this Job
                        </Button>
                    </Link>
                </Space>
                <div style={{ textAlign: 'center' }}>
                    <Text type="secondary">
                        Already have an account? <Link to="/auth/login">Sign in</Link>
                    </Text>
                </div>
                <AppStoreLinks />
            </Card>
        </AuthShell>
    );
};

export default JobPublicDetails;
